import React, { useEffect, useMemo, useState } from "react";
import { Table, Button, Tag, message, Modal, Form, Input, Popconfirm } from "antd";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";
import {
  UserAddOutlined,
  FileTextOutlined,
  PlusOutlined,
} from "@ant-design/icons";

const Invoices = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactForm] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get("/admin/invoices");
      const list = Array.isArray(data?.invoices)
        ? data.invoices
        : Array.isArray(data)
        ? data
        : [];
      setRows(list);
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to load invoices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDelete = async (id) => {
    try {
      await axiosInstance.delete(`/admin/invoices/${id}`);
      message.success("Invoice deleted");
      load();
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to delete invoice");
    }
  };

  const stats = useMemo(() => {
    const total = rows.length;
    const drafts = rows.filter((r) => !r.invoiceNo || String(r.status || "").toLowerCase() === "draft").length;
    const sentOrPaid = rows.filter((r) => {
      const val = String(r.status || "").toLowerCase();
      return val === "sent" || val === "paid";
    }).length;

    return { total, drafts, sentOrPaid };
  }, [rows]);

  const columns = [
    {
      title: "Invoice #",
      dataIndex: "invoiceNo",
      key: "num",
      render: (v) =>
        v ? (
          <span className="font-medium text-gray-900">{v}</span>
        ) : (
          <Tag color="default">DRAFT</Tag>
        ),
    },
    {
      title: "Issue Date",
      dataIndex: "issueDate",
      key: "i",
      render: (v) => <span className="text-gray-700">{v || "-"}</span>,
    },
    {
      title: "Customer",
      dataIndex: "customerName",
      key: "c",
      render: (v, r) => (
        <span className="font-medium text-gray-900">{v || r?.customer?.name || "-"}</span>
      ),
    },
    {
      title: "Total (£)",
      key: "t",
      render: (_, r) => (
        <span className="font-semibold text-gray-900">
          {Number(r.grandTotal ?? r.grand_total ?? 0).toFixed(2)}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "s",
      render: (v) => {
        const val = (v || "draft").toString().toLowerCase();
        return (
          <Tag
            color={
              val === "archived"
                ? "orange"
                : val === "sent" || val === "paid"
                ? "green"
                : "default"
            }
          >
            {val.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 180,
      render: (_, record) => {
        return (
          <div style={{ display: "flex", gap: 8 }}>
            <Button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/invoices/${record.id}`);
              }}
              className="rounded-lg"
            >
              Edit
            </Button>

            <Popconfirm
              title="Delete this invoice? This action is permanent."
              onConfirm={async (e) => {
                if (e && e.stopPropagation) e.stopPropagation();
                await handleDelete(record.id);
              }}
              onCancel={(e) => e && e.stopPropagation()}
              okText="Delete"
              cancelText="Cancel"
            >
              <Button
                danger
                className="rounded-lg"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                Delete
              </Button>
            </Popconfirm>
          </div>
        );
      },
    },
  ];

  const openAddContact = () => {
    contactForm.resetFields();
    setContactModalOpen(true);
  };

  const saveContact = async () => {
    try {
      const vals = await contactForm.validateFields();
      await axiosInstance.post("/admin/invoices/contacts", {
        name: vals.name?.trim(),
        address: vals.address?.trim() || "",
        vat: vals.vat?.trim() || "",
      });
      setContactModalOpen(false);
      message.success("Contact created");
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to create contact");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-6 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Invoices</h1>
              <p className="text-gray-600">
                Manage customer invoices, drafts and billing records
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                icon={<UserAddOutlined />}
                onClick={openAddContact}
                className="rounded-lg font-medium"
              >
                New Customer
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate("/invoices/manage")}
                className="bg-blue-500 hover:bg-blue-600 border-blue-500 text-white rounded-lg font-medium"
              >
                Create Invoice
              </Button>
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
            <div className="text-sm text-gray-500 mb-1">Total Invoices</div>
            <div className="text-2xl font-bold text-blue-600">{stats.total}</div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
            <div className="text-sm text-gray-500 mb-1">Draft Invoices</div>
            <div className="text-2xl font-bold text-orange-500">{stats.drafts}</div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
            <div className="text-sm text-gray-500 mb-1">Sent / Paid</div>
            <div className="text-2xl font-bold text-green-600">{stats.sentOrPaid}</div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-200 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <FileTextOutlined className="text-blue-600 text-lg" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 m-0">Invoice List</h2>
              <p className="text-sm text-gray-500 m-0">
                Click any row to open and edit invoice details
              </p>
            </div>
          </div>

          <Table
            rowKey="id"
            dataSource={rows}
            columns={columns}
            loading={loading}
            bordered={false}
            size="middle"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} invoices`,
              className: "px-6 py-4",
              pageSizeOptions: ["10", "20", "50", "100"],
            }}
            onRow={(record) => ({
              onClick: () => navigate(`/invoices/${record.id}`),
              className: "clickable-row",
            })}
            className="modern-invoices-table"
            locale={{
              emptyText: (
                <div className="text-center py-10">
                  <div className="text-lg font-medium text-gray-500 mb-2">No invoices found</div>
                  <div className="text-sm text-gray-400">
                    Create your first invoice to get started
                  </div>
                </div>
              ),
            }}
          />
        </div>
      </div>

      <Modal
        title={
          <div className="flex items-center space-x-2">
            <UserAddOutlined className="text-blue-500" />
            <span>Add Customer</span>
          </div>
        }
        open={contactModalOpen}
        onCancel={() => setContactModalOpen(false)}
        onOk={saveContact}
        okText="Save Contact"
        className="modern-modal"
      >
        <Form form={contactForm} layout="vertical">
          <Form.Item
            label="Name / Company"
            name="name"
            rules={[{ required: true, message: "Please enter a name" }]}
          >
            <Input className="rounded-lg" />
          </Form.Item>

          <Form.Item label="Address" name="address">
            <Input.TextArea rows={3} className="rounded-lg" />
          </Form.Item>

          <Form.Item
            label="Email"
            name="email"
            rules={[
              {
                type: "email",
                message: "Please enter a valid email address",
              },
            ]}
          >
            <Input placeholder="Enter email address" className="rounded-lg" />
          </Form.Item>

          <Form.Item label="VAT Number" name="vat">
            <Input className="rounded-lg" />
          </Form.Item>
        </Form>
      </Modal>

      <style>{`
        .clickable-row td {
          cursor: pointer;
        }

        .modern-invoices-table .ant-table-thead > tr > th {
          background: #f8fafc !important;
          color: #374151 !important;
          font-weight: 600;
          border-bottom: 2px solid #e5e7eb !important;
          white-space: nowrap;
          padding: 12px 16px !important;
        }

        .modern-invoices-table .ant-table-tbody > tr > td {
          border-bottom: 1px solid #f1f5f9 !important;
          padding: 12px 16px !important;
          transition: all 0.2s ease;
          background: #fff;
        }

        .modern-invoices-table .ant-table-tbody > tr.clickable-row:hover > td {
          background: #f9fafb !important;
        }

        .modern-invoices-table .ant-table-container {
          border: none !important;
        }

        .modern-modal .ant-modal-content {
          border-radius: 16px;
          overflow: hidden;
        }

        .modern-modal .ant-modal-header {
          border-bottom: 1px solid #e5e7eb;
          background: #f8fafc;
        }
      `}</style>
    </div>
  );
};

export default Invoices;